import { type FC, lazy } from "react";
import { matchRoutes } from "react-router";

import type { Waffles } from "@api/waffles";
import type { IconName } from "@components/Icon";
import type { Props } from "@components/NavigatorMenu";
import { ApplicationContextValues } from "./context/ApplicationContext.enum";

const AssetsManagementPage = lazy(() => import("@pages/AssetsManagementPage"));
const AuctionsListPage = lazy(() => import("@pages/AuctionsListPage"));
const AuctionDetailsPage = lazy(() => import("@pages/AuctionDetailsPage"));
const LotBidsListPage = lazy(() => import("@pages/LotBidsListPage"));
const HomePage = lazy(() => import("@pages/HomePage"));
const CreateAuctionPage = lazy(() => import("@pages/CreateAuctionPage"));
const RequestDisposalPage = lazy(() => import("@pages/RequestDisposalPage"));
const WhyToDisposePage = lazy(() => import("@pages/WhyToDisposePage"));
const ManageRequestsPage = lazy(() => import("@pages/ManageRequestsPage"));
const ManageUsers = lazy(() => import("@pages/ManageUsersPage/ManageUsers"));
const BidderDetailsPage = lazy(() => import("@pages/ManageUsersPage/UserDetails"));
const UserDetailsPage = lazy(() => import("@pages/ManageUsersPage/UserDetailsPage"));
const UserGuidePage = lazy(() => import("@pages/UserGuidePage"));
const Blank = lazy(() => import("@utils/Blank"));

export enum Routes {
  Home = "/",
  WhyToDispose = "/why-to-dispose",
  RequestDisposal = "/request-disposal",
  AuctionsList = "/auctions-list",
  AuctionDetails = "/auction-details/:id",
  AuctionAssetsAndLots = "/auction-assets-lots/:id",
  AuctionReport = "/auction-report/:id",
  LotBidsList = "/auction-report/:id/lot/:lotId/bids",
  CreateAuction = "/create-auction",
  ManageRequests = "/manage-requests",
  AssetsManagement = "/assets-management",
  ManageUsers = "/manage-users",
  UserDetailsPage = "/manage-users/:id",
  BidderDetailsPage = "/bidder-details/:id",
  UserGuide = "/user-guide",
}

type Route = {
  to: Routes;
  icon?: IconName;
  i18nLabelKey?: string | ((waffles: Waffles) => string);
  component: FC;
};
type RoutesFromContext = { [key in ApplicationContextValues]: Route[] };

/** Every page, regardless of application context. The menu links come from `ROUTES_FROM_CONTEXT`. */
export const PAGE_ROUTES: { to: Routes; component: FC }[] = [
  { to: Routes.Home, component: HomePage },
  { to: Routes.WhyToDispose, component: WhyToDisposePage },
  { to: Routes.RequestDisposal, component: RequestDisposalPage },
  { to: Routes.AuctionsList, component: AuctionsListPage },
  { to: Routes.AuctionDetails, component: AuctionDetailsPage },
  { to: Routes.AuctionAssetsAndLots, component: AuctionDetailsPage },
  { to: Routes.AuctionReport, component: AuctionDetailsPage },
  { to: Routes.LotBidsList, component: LotBidsListPage },
  { to: Routes.CreateAuction, component: CreateAuctionPage },
  { to: Routes.ManageRequests, component: ManageRequestsPage },
  { to: Routes.AssetsManagement, component: AssetsManagementPage },
  { to: Routes.ManageUsers, component: ManageUsers },
  { to: Routes.UserGuide, component: UserGuidePage },
  { to: Routes.UserDetailsPage, component: UserDetailsPage },
  { to: Routes.BidderDetailsPage, component: BidderDetailsPage },
];

export const CONTEXT_LINK: { [key in Routes]: ApplicationContextValues[] } = {
  [Routes.Home]: [ApplicationContextValues.Public, ApplicationContextValues.DigitalOffice],
  [Routes.WhyToDispose]: [ApplicationContextValues.Public],
  [Routes.RequestDisposal]: [ApplicationContextValues.Public],
  [Routes.AuctionsList]: [ApplicationContextValues.DigitalOffice],
  [Routes.AuctionDetails]: [ApplicationContextValues.DigitalOffice],
  [Routes.AuctionAssetsAndLots]: [ApplicationContextValues.DigitalOffice],
  [Routes.AuctionReport]: [ApplicationContextValues.DigitalOffice],
  [Routes.LotBidsList]: [ApplicationContextValues.DigitalOffice],
  [Routes.CreateAuction]: [ApplicationContextValues.DigitalOffice],
  [Routes.ManageRequests]: [ApplicationContextValues.DigitalOffice],
  [Routes.AssetsManagement]: [ApplicationContextValues.DigitalOffice],
  [Routes.ManageUsers]: [ApplicationContextValues.DigitalOffice],
  [Routes.UserDetailsPage]: [ApplicationContextValues.DigitalOffice],
  [Routes.BidderDetailsPage]: [ApplicationContextValues.DigitalOffice],
  [Routes.UserGuide]: [ApplicationContextValues.DigitalOffice],
};

// `matchRoutes` wants route objects, not bare patterns. Derived from CONTEXT_LINK itself
// rather than re-listed, so the matcher and the context map cannot drift apart.
const CONTEXT_ROUTE_OBJECTS = (Object.keys(CONTEXT_LINK) as Routes[]).map((path) => ({ path }));

/**
 * Recovers the `Routes` pattern a pathname was produced from, and returns the application
 * contexts allowed for it.
 *
 * Uses react-router's own matcher so params of ANY shape resolve - `/auction-details/123` and
 * `/auction-details/IT-001` both land on `Routes.AuctionDetails`.
 */
export function getContextsForPathname(pathname: string): ApplicationContextValues[] | undefined {
  const matches = matchRoutes(CONTEXT_ROUTE_OBJECTS, pathname);
  // The pattern list is flat, so there is always exactly one match; reading the last entry is
  // defensive in case the patterns are ever nested.
  const matchedPath = matches?.[matches.length - 1]?.route.path;

  return matchedPath ? CONTEXT_LINK[matchedPath] : undefined;
}

const ROUTES_FROM_CONTEXT: RoutesFromContext = {
  [ApplicationContextValues.Public]: [
    {
      to: Routes.Home,
      icon: "Home",
      i18nLabelKey: "menu.home",
      component: HomePage,
    },
    {
      to: Routes.WhyToDispose,
      icon: "Handshake",
      i18nLabelKey: "menu.whyToDispose",
      component: WhyToDisposePage,
    },
    {
      to: Routes.RequestDisposal,
      icon: "Hammer",
      i18nLabelKey: "menu.requestDisposal",
      component: RequestDisposalPage,
    },
  ],
  [ApplicationContextValues.DigitalOffice]: [
    {
      to: Routes.Home,
      icon: "Lock",
      i18nLabelKey: "menu.digitalOfficeHome",
      component: HomePage,
    },
    {
      to: Routes.CreateAuction,
      icon: "Hammer",
      i18nLabelKey: "menu.createAuction",
      component: Blank,
    },
    {
      to: Routes.ManageRequests,
      icon: "File",
      i18nLabelKey: "menu.manageRequests",
      component: ManageRequestsPage,
    },
    {
      to: Routes.AuctionsList,
      icon: "ClipboardCheck",
      i18nLabelKey: "menu.auctionsList",
      component: AuctionsListPage,
    },
    {
      to: Routes.AuctionDetails,
      component: AuctionDetailsPage,
    },
    {
      to: Routes.AuctionAssetsAndLots,
      component: AuctionDetailsPage,
    },
    {
      to: Routes.AuctionReport,
      component: AuctionDetailsPage,
    },
    {
      to: Routes.LotBidsList,
      component: LotBidsListPage,
    },
    {
      to: Routes.AssetsManagement,
      icon: "Folder",
      i18nLabelKey: "menu.assetsManagement",
      component: AssetsManagementPage,
    },
    {
      to: Routes.ManageUsers,
      icon: "User",
      i18nLabelKey: (waffles: Waffles) =>
        waffles.switches["manage-users-page"] ? "menu.manageUsers" : "menu.manageBidders",
      component: ManageUsers,
    },
    {
      to: Routes.UserGuide,
      icon: "Education",
      i18nLabelKey: "menu.userGuide",
      component: UserGuidePage,
    },
  ],
};

export function getLinks(context: ApplicationContextValues) {
  return ROUTES_FROM_CONTEXT[context]
    .filter((item) => Boolean(item.i18nLabelKey) && Boolean(item.icon))
    .map(({ component, ...link }) => link) as Props["links"];
}
